import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { connect, useDispatch } from 'react-redux';

import { BaseMultipleSelectorWithPrompt } from 'components/base/BaseMultipleSelectorV2';
import { FieldControl } from 'components/base/CommonComponent';
import CustomDateTimeSelect from 'components/base/CustomDateTimeSelect';
import CustomTitleWithSwitch from 'components/base/CustomTitleWithSwitch';
import {
  ReactHookFormErrorMessage,
  hasError,
} from 'components/base/ErrorFieldMessage';
import { CustomTitleLabel } from 'components/earning/CustomBaseComponments';
import { DISPLAY_MESSAGE_TARGET_TYPE, MESSAGE_TARGET_TYPE, MessageChannel, SEGMENT_TYPE } from 'config/CustomEnums';
import { PermissionCodes } from 'config/PermissionCodes';
import ReactHookFormTitleWithRadio from 'components/reactHookFormComponents/ReactHookFormTitleWithRadio';
import { createAction } from 'utils';
import ReactHookFormSwitch from 'components/reactHookFormComponents/ReactHookFormSwitch';

const mapPropsToState = (state) => ({
  segmentList: state.segments.notPagedAllList,
  levelList: state.levels.levelList,
});

function MessageSettingSection({
  segmentList,
  levelList,
}) {
  const dispatch = useDispatch();

  const { watch, setValue, formState } = useFormContext();
  const { errors } = formState;

  const targetTypeFieldName = "targetType"
  const watchTargetType = watch(targetTypeFieldName)
  const targetedDynamicSegmentsFieldName = "targetedDynamicSegments";
  const targetedStaticSegmentsFieldName = "targetedStaticSegments";
  const targetedDynamicSegments = watch(targetedDynamicSegmentsFieldName) || [];
  const targetedStaticSegments = watch(targetedStaticSegmentsFieldName) || [];

  const targetedSegmentsFieldName = watchTargetType === MESSAGE_TARGET_TYPE.STATIC_SEGMENT
    ? targetedStaticSegmentsFieldName : targetedDynamicSegmentsFieldName;
  const targetedSegments = watchTargetType === MESSAGE_TARGET_TYPE.STATIC_SEGMENT
    ? targetedStaticSegments : targetedDynamicSegments;

  const targetedLevelsFieldName = "targetedLevels"
  const watchTargetedLevels = watch(targetedLevelsFieldName) || [];
  const scheduledDate = watch('scheduledDate');
  const channels = watch('channels');
  const sendToAll = watch('sendToAll');
  const recipientsFollowCampaign = watch('recipientsFollowCampaign')
  const watchIsDirectMarketing = watch('isDirectMarketing');
  const relatedCampaign = watch('relatedCampaign');

  const disableTatgetCustomerSelector = sendToAll || recipientsFollowCampaign;

  useEffect(() => {
    if (watchTargetType === MESSAGE_TARGET_TYPE.DYNAMIC_SEGMENT) {
      dispatch(createAction("segments/getAllList")({
        moreSearch: {
          type: SEGMENT_TYPE.DYNAMIC,
          isBlacklist: false,
        }
      }))
      return
    }
    if (watchTargetType === MESSAGE_TARGET_TYPE.STATIC_SEGMENT) {
      dispatch(createAction("segments/getAllList")({
        moreSearch: {
          type: SEGMENT_TYPE.STATIC,
          isBlacklist: false,
        }
      }))
      return
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchTargetType])

  useEffect(() => {
    if (!channels?.includes(MessageChannel.email)) {
      setValue('isDirectMarketing', false, { shouldDirty: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channels]);

  useEffect(() => {
    if (!relatedCampaign?.isExclusive) {
      setValue('recipientsFollowCampaign', false, { shouldDirty: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relatedCampaign]);

  console.log("message campaign:", relatedCampaign)

  const getTargetCustomerSelector = () => {
    if ([MESSAGE_TARGET_TYPE.DYNAMIC_SEGMENT, MESSAGE_TARGET_TYPE.STATIC_SEGMENT].includes(watchTargetType)) {
      return <BaseMultipleSelectorWithPrompt
        title={DISPLAY_MESSAGE_TARGET_TYPE[watchTargetType]}
        namespace={"segments"}
        loadSourceDataConfig={{
          action: "segments/getAllList",
          filter: {
            moreSearch: {
              isBlacklist: false,
              type: watchTargetType === MESSAGE_TARGET_TYPE.DYNAMIC_SEGMENT
                ? SEGMENT_TYPE.DYNAMIC
                : SEGMENT_TYPE.STATIC
            }
          },
        }}
        data={{
          sourceData: segmentList,
          targetData: disableTatgetCustomerSelector ? [] : targetedSegments,
          targetChange: (options) => {
            setValue(targetedSegmentsFieldName, options, { shouldDirty: true });
          }
        }}
        addButton={{
          title: 'Add Segments',
        }}
        promptConfig={{
          title: `Go to create segment ?`,
          description: `You will leave message creation process.`,
          rightButton: {
            text: `Go to Create Segment`,
            action: () => {
              window.open("/segments/create/", '_blank');
            },
          }
        }}
        requires={PermissionCodes.addCustomerGroup}
        disabled={disableTatgetCustomerSelector}
      />
    }
    if (watchTargetType === MESSAGE_TARGET_TYPE.LEVEL) {
      return <BaseMultipleSelectorWithPrompt
        namespace={"levels"}
        title={DISPLAY_MESSAGE_TARGET_TYPE[watchTargetType]}
        loadSourceDataConfig={{
          action: "levels/getLevelList",
          filter: {},
        }}
        data={{
          sourceData: levelList,
          targetData: disableTatgetCustomerSelector ? [] : watchTargetedLevels,
          targetChange: (options) => {
            setValue(targetedLevelsFieldName, options, { shouldDirty: true });
          }
        }}
        disabled={disableTatgetCustomerSelector}
      />
    }
  }

  return (
    <>
      <label className="create-section-title">{'SETTING'}</label>

      {channels?.find((item) => item === MessageChannel.push) && !relatedCampaign?.isExclusive ? (
        <ReactHookFormSwitch
          title="Send to every customer (including non-member)"
          valueFieldName={"sendToAll"}
          extraActionOnChange={({ value }) => {
            if (value) {
              setValue('recipientsFollowCampaign', !value, { shouldDirty: true });
            }
          }}
        />
      ) : null}

      {relatedCampaign?.isExclusive && (
        <ReactHookFormSwitch
          title="Message recipients follow campaign"
          tips={"If toggle off, the message will be delivered to customers targeted in both the campaign & message."}
          valueFieldName={"recipientsFollowCampaign"}
          extraActionOnChange={({ value }) => {
            if (value) {
              setValue('sendToAll', !value, { shouldDirty: true });
            }
          }}
        />
      )}

      {channels?.includes(MessageChannel.email) && (
        <FieldControl
          name="isDirectMarketing"
          render={() => (
            <CustomTitleWithSwitch
              title={'Is this a direct marketing message?'}
              tips={
                'If toggle on, this message will regard as direct marketing message. Only those customers who has opt-in to receive direct marketing message, could see receive the message.'
              }
              defaultValue={!!watchIsDirectMarketing}
              setValue={(value) => {
                setValue('isDirectMarketing', value, { shouldDirty: true });
              }}
            />
          )}
        />
      )}
      <ReactHookFormTitleWithRadio
        title={"Target customer"}
        options={[
          MESSAGE_TARGET_TYPE.DYNAMIC_SEGMENT,
          MESSAGE_TARGET_TYPE.STATIC_SEGMENT,
          MESSAGE_TARGET_TYPE.LEVEL,
        ]?.map(type => ({
          label: DISPLAY_MESSAGE_TARGET_TYPE?.[type] || type,
          value: type,
        }))}
        defaultValue={MESSAGE_TARGET_TYPE.DYNAMIC_SEGMENT}
        valueFieldName={targetTypeFieldName}
        disabled={disableTatgetCustomerSelector}
      />

      {getTargetCustomerSelector()}

      <CustomTitleLabel title="Schedule time" />
      <CustomDateTimeSelect
        defaultTime={scheduledDate ? new Date(scheduledDate) : new Date()}
        onTimeChange={(datetime) => {
          setValue('scheduledDate', datetime, { shouldDirty: true });
        }}
        error={hasError(errors, 'scheduledDate')}
      />
      <ReactHookFormErrorMessage errors={errors} id="scheduledDate" />
    </>
  );
}

export default connect(mapPropsToState)(MessageSettingSection);
